import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API, getImageUrl } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Users, Download, FileText, Loader2, Upload, UserPlus, Trash2,
  ClipboardCheck, Pencil, Plus, Mail, Eye, Check, Copy, KeyRound,
  MessageCircle
} from 'lucide-react';
import PlanBanner from '../../components/PlanBanner';

const StudentsList = () => {
  const [students, setStudents] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showImportResultsDialog, setShowImportResultsDialog] = useState(false);
  const [showEvalDialog, setShowEvalDialog] = useState(false);
  const [showViewEvalsDialog, setShowViewEvalsDialog] = useState(false);
  const [viewEvalsData, setViewEvalsData] = useState([]);
  const [viewEvalsLoading, setViewEvalsLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [companyPlan, setCompanyPlan] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cpf: '',
    phone: '',
    cellphone: '',
    birth_date: '',
    department: ''
  });
  const [evalData, setEvalData] = useState({
    weight: '', height: '', body_fat_percentage: '', lean_mass: '',
    chest: '', waist: '', hip: '', abdomen: '', right_arm: '', left_arm: '',
    right_thigh: '', left_thigh: '', cardio_test_result: '', strength_test_result: '', evaluator_name: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [studentsRes, gymsRes, planRes] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/gyms`),
        axios.get(`${API}/companies/plan`).catch(() => ({ data: {} }))
      ]);
      setStudents(studentsRes.data);
      setGyms(gymsRes.data);
      setCompanyPlan(planRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(
        `${API}/students`,
        {
          name: formData.name,
          email: formData.email || null,
          cpf: formData.cpf || null,
          phone: formData.phone || null,
          cellphone: formData.cellphone || null,
          birth_date: formData.birth_date || null,
          department: formData.department || null
        }
      );

      toast.success('Colaborador cadastrado com sucesso!');
      setSelectedStudent(response.data);
      setShowDialog(false);
      setShowCodeDialog(true);
      setFormData({ name: '', email: '', cpf: '', phone: '', cellphone: '', birth_date: '', department: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating student:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar colaborador');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/students/template/download`, {
        
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_colaboradores.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template baixado!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/students/import?send_emails=true`, formData, {
        
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResults(response.data);
      setShowImportDialog(false);
      setShowImportResultsDialog(true);
      fetchData();
      
      if (response.data.success > 0) {
        toast.success(`${response.data.success} colaboradores importados!`);
      }
      if (response.data.emails_sent > 0) {
        toast.success(`${response.data.emails_sent} emails enviados!`);
      }
      if (response.data.errors > 0) {
        toast.error(`${response.data.errors} erros durante importação`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleDownloadResults = () => {
    if (!importResults?.results?.length) return;
    
    // Create CSV content
    const headers = ['Nome', 'Email', 'CPF', 'Data de Nascimento', 'Departamento', 'Código de Acesso', 'Email Enviado'];
    const rows = importResults.results.map(r => [
      r.name, r.email, r.cpf, r.birth_date, r.department, r.access_code, r.email_sent
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'colaboradores_importados.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Arquivo baixado!');
  };

  const handleAssignGym = async (studentId, gymId) => {
    try {
      await axios.put(
        `${API}/students/${studentId}/assign-gym?gym_id=${gymId}`,
        {}
      );
      toast.success('Academia vinculada com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao vincular academia');
    }
  };

  const handleDeleteStudent = async (student) => {
    if (!window.confirm(`Deseja realmente excluir ${student.name}? Esta ação é irreversível.`)) return;
    try {
      await axios.delete(`${API}/students/${student.student_id}`);
      toast.success('Colaborador removido com sucesso!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir colaborador');
    }
  };

  const handleOpenEvaluation = (student) => {
    setSelectedStudent(student);
    setEvalData({
      weight: '', height: '', body_fat_percentage: '', lean_mass: '',
      chest: '', waist: '', hip: '', abdomen: '', right_arm: '', left_arm: '',
      right_thigh: '', left_thigh: '', cardio_test_result: '', strength_test_result: '', evaluator_name: ''
    });
    setShowEvalDialog(true);
  };

  const handleSubmitEvaluation = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/evaluations`, {
        student_id: selectedStudent.student_id,
        weight: parseFloat(evalData.weight),
        height: parseFloat(evalData.height),
        body_fat_percentage: parseFloat(evalData.body_fat_percentage),
        lean_mass: parseFloat(evalData.lean_mass),
        chest: evalData.chest ? parseFloat(evalData.chest) : null,
        waist: evalData.waist ? parseFloat(evalData.waist) : null,
        hip: evalData.hip ? parseFloat(evalData.hip) : null,
        abdomen: evalData.abdomen ? parseFloat(evalData.abdomen) : null,
        right_arm: evalData.right_arm ? parseFloat(evalData.right_arm) : null,
        left_arm: evalData.left_arm ? parseFloat(evalData.left_arm) : null,
        right_thigh: evalData.right_thigh ? parseFloat(evalData.right_thigh) : null,
        left_thigh: evalData.left_thigh ? parseFloat(evalData.left_thigh) : null,
        cardio_test_result: evalData.cardio_test_result || null,
        strength_test_result: evalData.strength_test_result || null,
        evaluator_name: evalData.evaluator_name || null
      });
      toast.success('Avaliação registrada com sucesso!');
      setShowEvalDialog(false);
    } catch (error) {
      toast.error('Erro ao registrar avaliação');
    } finally {
      setSubmitting(false);
    }
  };

  const getGymName = (gymId) => {
    const gym = gyms.find(g => g.gym_id === gymId);
    return gym ? gym.name : null;
  };

  const handleViewEvaluations = async (student) => {
    setSelectedStudent(student);
    setViewEvalsLoading(true);
    setShowViewEvalsDialog(true);
    try {
      const res = await axios.get(`${API}/evaluations/student/${student.student_id}?limit=3`);
      setViewEvalsData(res.data);
    } catch {
      toast.error('Erro ao carregar avaliações');
    } finally {
      setViewEvalsLoading(false);
    }
  };

  const handleSendCodeEmail = async (student) => {
    try {
      const res = await axios.post(`${API}/students/${student.student_id}/send-code-email`, {});
      if (res.data.fallback) {
        toast.info(`Email falhou. Código: ${res.data.access_code}`, { duration: 8000 });
      } else {
        toast.success(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar email');
    }
  };

  const handleSendWhatsApp = async (student) => {
    try {
      const res = await axios.post(`${API}/students/${student.student_id}/send-whatsapp-code`, {});
      if (res.data.fallback && res.data.whatsapp_url) {
        window.open(res.data.whatsapp_url, '_blank');
        toast.info('Envio automático falhou. Link manual aberto.');
      } else {
        toast.success(`Código enviado via WhatsApp para ${res.data.phone || 'o aluno'}!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar WhatsApp');
    }
  };

  const showAccessCode = (student) => {
    setSelectedStudent(student);
    setShowCodeDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Colaboradores
          </h1>
          <p className="text-slate-600 mt-1">
            Cadastre e gerencie os colaboradores
            {companyPlan?.max_students > 0 && (
              <span className="ml-2 text-sm font-medium text-blue-600">
                ({students.length}/{companyPlan.max_students} vagas)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline"
            onClick={() => setShowImportDialog(true)}
            data-testid="import-students-btn"
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importar Excel
          </Button>
          <Button 
            onClick={() => setShowDialog(true)}
            data-testid="add-student-btn"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Novo Colaborador
          </Button>
        </div>
      </div>

      {students.length === 0 ? (
        <Card className="border-slate-100">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhum colaborador ainda</h3>
            <p className="text-slate-500 mb-4">Cadastre colaboradores para que possam acessar a plataforma.</p>
            <Button 
              onClick={() => setShowDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Cadastrar Primeiro Colaborador
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-100">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Nome</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Departamento</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.student_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                        <div className="flex items-center gap-2.5">
                          {student.profile_photo ? (
                            <img src={getImageUrl(student.profile_photo)} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-slate-400">
                              {(student.name || 'N')[0]}
                            </div>
                          )}
                          {student.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{student.email}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{student.department || '-'}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" size="sm" title="Ver código de acesso"
                            onClick={() => showAccessCode(student)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`code-btn-${student.student_id}`}
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" size="sm" title="Enviar código por email"
                            onClick={() => handleSendCodeEmail(student)}
                            className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                            data-testid={`email-btn-${student.student_id}`}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" size="sm" title="Enviar código via WhatsApp"
                            onClick={() => handleSendWhatsApp(student)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            data-testid={`whatsapp-btn-${student.student_id}`}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" size="sm" title="Ver avaliações"
                            onClick={() => handleViewEvaluations(student)}
                            className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                            data-testid={`view-evals-btn-${student.student_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" size="sm" title="Nova avaliação física"
                            onClick={() => handleOpenEvaluation(student)}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            data-testid={`eval-btn-${student.student_id}`}
                          >
                            <ClipboardCheck className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" size="sm" title="Excluir colaborador"
                            onClick={() => handleDeleteStudent(student)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-btn-${student.student_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Student Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Cadastrar Colaborador
            </DialogTitle>
            <DialogDescription>
              O colaborador receberá um código de acesso único
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="student-name">Nome Completo *</Label>
              <Input
                id="student-name"
                data-testid="student-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: João Silva"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="student-email">Email *</Label>
              <Input
                id="student-email"
                type="email"
                data-testid="student-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="joao@email.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="student-cpf">CPF (opcional)</Label>
              <Input
                id="student-cpf"
                data-testid="student-cpf-input"
                value={formData.cpf}
                onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                placeholder="000.000.000-00"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="student-phone">Telefone</Label>
                <Input
                  id="student-phone"
                  data-testid="student-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(00) 0000-0000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="student-cellphone">Celular</Label>
                <Input
                  id="student-cellphone"
                  data-testid="student-cellphone-input"
                  value={formData.cellphone}
                  onChange={(e) => setFormData({ ...formData, cellphone: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="student-birth">Data de Nascimento (opcional)</Label>
              <Input
                id="student-birth"
                data-testid="student-birth-input"
                value={formData.birth_date}
                onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                placeholder="DD/MM/AAAA"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="student-department">Departamento (opcional)</Label>
              <Input
                id="student-department"
                data-testid="student-department-input"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                placeholder="Ex: TI, RH, Vendas"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="submit-student-btn"
                disabled={submitting || !formData.name}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Cadastrar'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Access Code Dialog */}
      <Dialog open={showCodeDialog} onOpenChange={setShowCodeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <KeyRound className="w-5 h-5 text-emerald-600" />
              Código de Acesso
            </DialogTitle>
            <DialogDescription>
              {selectedStudent?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6 text-center">
            <div className="bg-slate-100 rounded-xl p-6 mb-4">
              <p className="text-4xl font-mono font-bold tracking-widest text-slate-900">
                {selectedStudent?.access_code}
              </p>
            </div>
            <p className="text-sm text-slate-600">
              Compartilhe este código com o colaborador para que ele possa acessar a plataforma.
            </p>
          </div>

          <Button
            onClick={() => {
              navigator.clipboard.writeText(selectedStudent?.access_code);
              toast.success('Código copiado!');
            }}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            Copiar Código
          </Button>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Upload className="w-5 h-5 text-emerald-600" />
              Importar Colaboradores
            </DialogTitle>
            <DialogDescription>
              Importe colaboradores em massa usando uma planilha Excel
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-2">Formato da Planilha</h4>
              <p className="text-sm text-slate-600 mb-3">
                A planilha deve conter as seguintes colunas:
              </p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• <strong>Nome</strong> (obrigatório)</li>
                <li>• <strong>Data de Nascimento</strong> (formato: DD/MM/AAAA)</li>
                <li>• <strong>CPF</strong></li>
                <li>• <strong>Departamento</strong></li>
              </ul>
            </div>

            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Baixar Template Excel
            </Button>

            <div className="relative">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFile}
                disabled={importing}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                data-testid="import-file-input"
              />
              <Button
                disabled={importing}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {importing ? 'Importando...' : 'Selecionar Arquivo'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Results Dialog */}
      <Dialog open={showImportResultsDialog} onOpenChange={setShowImportResultsDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Resultado da Importação
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{importResults?.success || 0}</p>
                <p className="text-xs text-emerald-700">Importados</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{importResults?.emails_sent || 0}</p>
                <p className="text-xs text-blue-700">Emails</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{importResults?.errors || 0}</p>
                <p className="text-xs text-red-700">Erros</p>
              </div>
            </div>

            {importResults?.error_details?.length > 0 && (
              <div className="bg-red-50 rounded-xl p-4">
                <h4 className="font-medium text-red-900 mb-2">Erros encontrados:</h4>
                <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                  {importResults.error_details.map((err, idx) => (
                    <li key={idx}>Linha {err.row}: {err.name} - {err.error}</li>
                  ))}
                </ul>
              </div>
            )}

            {importResults?.results?.length > 0 && (
              <Button
                onClick={handleDownloadResults}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar Lista com Códigos de Acesso
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Evaluation Dialog */}
      <Dialog open={showEvalDialog} onOpenChange={setShowEvalDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <ClipboardCheck className="w-5 h-5 text-emerald-600" />
              Avaliação Física - {selectedStudent?.name}
            </DialogTitle>
            <DialogDescription>Registre os dados da avaliação</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEvaluation} className="space-y-5 mt-2">
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Medidas Básicas</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'weight', label: 'Peso (kg)', ph: '70.5', req: true },
                  { key: 'height', label: 'Altura (cm)', ph: '175', req: true },
                  { key: 'body_fat_percentage', label: 'Gordura (%)', ph: '18.5', req: true },
                  { key: 'lean_mass', label: 'Massa Magra (kg)', ph: '55', req: true }
                ].map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label htmlFor={`eval-${f.key}`}>{f.label} {f.req && '*'}</Label>
                    <Input id={`eval-${f.key}`} type="number" step="0.1" value={evalData[f.key]}
                      onChange={(e) => setEvalData({ ...evalData, [f.key]: e.target.value })} placeholder={f.ph} required={f.req} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Circunferências (cm)</h4>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'chest', label: 'Tórax' }, { key: 'waist', label: 'Cintura' },
                  { key: 'hip', label: 'Quadril' }, { key: 'abdomen', label: 'Abdômen' },
                  { key: 'right_arm', label: 'Braço D' }, { key: 'left_arm', label: 'Braço E' },
                  { key: 'right_thigh', label: 'Coxa D' }, { key: 'left_thigh', label: 'Coxa E' }
                ].map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label htmlFor={`eval-${f.key}`}>{f.label}</Label>
                    <Input id={`eval-${f.key}`} type="number" step="0.1" value={evalData[f.key]}
                      onChange={(e) => setEvalData({ ...evalData, [f.key]: e.target.value })} />
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Teste Cardiorrespiratório</Label>
                <Input value={evalData.cardio_test_result}
                  onChange={(e) => setEvalData({ ...evalData, cardio_test_result: e.target.value })} placeholder="Bom, Regular..." />
              </div>
              <div className="space-y-1">
                <Label>Teste de Força</Label>
                <Input value={evalData.strength_test_result}
                  onChange={(e) => setEvalData({ ...evalData, strength_test_result: e.target.value })} placeholder="Bom, Regular..." />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Avaliador</Label>
              <Input value={evalData.evaluator_name}
                onChange={(e) => setEvalData({ ...evalData, evaluator_name: e.target.value })} placeholder="Nome do avaliador" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowEvalDialog(false)} className="flex-1">Cancelar</Button>
              <Button type="submit" data-testid="submit-eval-btn" disabled={submitting || !evalData.weight || !evalData.height || !evalData.body_fat_percentage || !evalData.lean_mass}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar Avaliação'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Evaluations Dialog */}
      <Dialog open={showViewEvalsDialog} onOpenChange={setShowViewEvalsDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Eye className="w-5 h-5 text-cyan-600" />
              Últimas Avaliações - {selectedStudent?.name}
            </DialogTitle>
            <DialogDescription>Resultados das 3 últimas avaliações físicas</DialogDescription>
          </DialogHeader>
          {viewEvalsLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
          ) : viewEvalsData.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nenhuma avaliação registrada</p>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              {viewEvalsData.map((ev, idx) => (
                <div key={ev.evaluation_id || idx} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-900">
                      Avaliação {idx + 1} — {ev.created_at ? new Date(ev.created_at).toLocaleDateString('pt-BR') : '-'}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      ev.evolution_classification === 'high' ? 'bg-emerald-100 text-emerald-700' :
                      ev.evolution_classification === 'moderate' ? 'bg-blue-100 text-blue-700' :
                      ev.evolution_classification === 'regression' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {ev.evolution_classification === 'high' ? 'Alta evolução' :
                       ev.evolution_classification === 'moderate' ? 'Moderada' :
                       ev.evolution_classification === 'regression' ? 'Regressão' : 'Estável'}
                      {ev.evolution_percentage ? ` (${ev.evolution_percentage > 0 ? '+' : ''}${ev.evolution_percentage.toFixed(1)}%)` : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-slate-500">Peso:</span> <strong>{ev.weight} kg</strong></div>
                    <div><span className="text-slate-500">Altura:</span> <strong>{ev.height} cm</strong></div>
                    <div><span className="text-slate-500">IMC:</span> <strong>{ev.imc?.toFixed(1)}</strong></div>
                    <div><span className="text-slate-500">Gordura:</span> <strong>{ev.body_fat_percentage}%</strong></div>
                    <div><span className="text-slate-500">Massa Magra:</span> <strong>{ev.lean_mass} kg</strong></div>
                    {ev.waist_hip_ratio && <div><span className="text-slate-500">Cintura/Quadril:</span> <strong>{ev.waist_hip_ratio}</strong></div>}
                    {ev.chest && <div><span className="text-slate-500">Tórax:</span> <strong>{ev.chest} cm</strong></div>}
                    {ev.waist && <div><span className="text-slate-500">Cintura:</span> <strong>{ev.waist} cm</strong></div>}
                    {ev.hip && <div><span className="text-slate-500">Quadril:</span> <strong>{ev.hip} cm</strong></div>}
                    {ev.abdomen && <div><span className="text-slate-500">Abdômen:</span> <strong>{ev.abdomen} cm</strong></div>}
                    {ev.right_arm && <div><span className="text-slate-500">Braço D:</span> <strong>{ev.right_arm} cm</strong></div>}
                    {ev.left_arm && <div><span className="text-slate-500">Braço E:</span> <strong>{ev.left_arm} cm</strong></div>}
                    {ev.right_thigh && <div><span className="text-slate-500">Coxa D:</span> <strong>{ev.right_thigh} cm</strong></div>}
                    {ev.left_thigh && <div><span className="text-slate-500">Coxa E:</span> <strong>{ev.left_thigh} cm</strong></div>}
                  </div>
                  {(ev.cardio_test_result || ev.strength_test_result || ev.evaluator_name) && (
                    <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-3 gap-3 text-sm">
                      {ev.cardio_test_result && <div><span className="text-slate-500">Cardio:</span> <strong>{ev.cardio_test_result}</strong></div>}
                      {ev.strength_test_result && <div><span className="text-slate-500">Força:</span> <strong>{ev.strength_test_result}</strong></div>}
                      {ev.evaluator_name && <div><span className="text-slate-500">Avaliador:</span> <strong>{ev.evaluator_name}</strong></div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Sector Ranking Component

export default StudentsList;
